/**
 * CONSAI Business App — AI Analytics Dashboard
 * Redesigned: Aether Command aesthetic (per user design prompts)
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { GlassCard } from '../components/ui/glass-card';
import {
  BarChart3, TrendingUp, TrendingDown, Users, Package,
  AlertTriangle, Crown, Lock, IndianRupee, Activity,
  Heart, Calendar, Loader2, Zap, Shield, RefreshCw,
  ArrowRight, ChevronDown, Filter, Download, Check
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, AreaChart, Area
} from 'recharts';
import Sidebar from '../components/Sidebar';
import MobileNav from '../components/MobileNav';
import { AetherPage } from '../components/AetherPage';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* ── helpers ────────────────────────────────────────────── */
const fmt = (n) => (n || 0).toLocaleString('en-IN');

const ChartTooltipStyle = {
  background: '#1C1B1B',
  border: '1px solid #262626',
  borderRadius: '8px',
  fontFamily: 'Manrope',
  fontSize: 12,
  color: '#F4F4F5',
};

/* ── Shared: loading spinner for each tab ───────────────── */
const TabLoader = ({ color = '#3B82F6' }) => (
  <div className="flex justify-center items-center py-20">
    <Loader2 className="h-8 w-8 animate-spin" style={{ color }} />
  </div>
);

/* ── Shared: "upgrade to premium" card ─────────────────── */
const PremiumLock = ({ navigate }) => (
  <GlassCard className="p-8 text-center">
    <Lock className="h-10 w-10 text-amber-400 mx-auto mb-4" />
    <p className="headline-sm text-[#F4F4F5] mb-2">Unlock Advanced Analytics</p>
    <p className="text-sm text-[#8A8A93] mb-6 max-w-sm mx-auto">
      Upgrade to Premium for Sales Heatmap, Customer LTV, Inventory Turnover, Credit Health Score, and Revenue Forecasting.
    </p>
    <button
      onClick={() => navigate('/payment?type=premium')}
      className="px-6 py-2.5 rounded-lg bg-white text-[#0A0A0B] text-sm font-semibold hover:bg-white/90 transition-colors inline-flex items-center gap-2"
      data-testid="upgrade-analytics-btn"
    >
      <Crown className="h-4 w-4" />
      Upgrade to Premium
    </button>
  </GlassCard>
);

/* ═══════════════════════════════════════════════════════════
   OVERVIEW TAB
═══════════════════════════════════════════════════════════ */
const OverviewTab = ({ data, language }) => {
  const kpi = [
    {
      feature: 'sales',
      label: 'TODAY SALES',
      value: `₹${fmt(data?.today_sales_total)}`,
      sub: `${data?.today_sales_count || 0} transactions processed today`,
      icon: TrendingUp,
      iconColor: '#22C55E',
      testId: 'stat-card-0',
    },
    {
      feature: 'analytics',
      label: 'THIS MONTH',
      value: `₹${fmt(data?.month_sales_total)}`,
      sub: `${data?.month_sales_count || 0} sales recorded`,
      icon: IndianRupee,
      iconColor: '#3B82F6',
      testId: 'stat-card-1',
    },
    {
      feature: 'customer',
      label: 'CUSTOMERS',
      value: data?.total_customers || 0,
      sub: `New registrations: 0`,
      icon: Users,
      iconColor: '#06B6D4',
      testId: 'stat-card-2',
    },
    {
      feature: 'stock',
      label: 'LOW STOCK',
      value: data?.low_stock_items || 0,
      sub: `${data?.total_inventory_items || 0} total tracked items`,
      icon: AlertTriangle,
      iconColor: '#F59E0B',
      testId: 'stat-card-3',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Section intro */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="label-caps text-[#8A8A93]">EXECUTIVE INTELLIGENCE</p>
          <p className="text-sm text-[#8A8A93] mt-1">
            Advanced performance metrics and real-time business insights.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
          <span className="label-caps text-green-400">LIVE SYNC</span>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="analytics-stats">
        {kpi.map((card) => (
          <GlassCard
            key={card.testId}
            feature={card.feature}
            className="p-5 pt-6"
            data-testid={card.testId}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="label-caps text-[#8A8A93]">{card.label}</p>
                <p className="display-md text-[#F4F4F5] mt-2 truncate">{card.value}</p>
                <p className="text-xs text-[#8A8A93] mt-1 leading-relaxed">{card.sub}</p>
              </div>
              <card.icon className="h-5 w-5 shrink-0 mt-1" style={{ color: card.iconColor }} />
            </div>
          </GlassCard>
        ))}
      </div>

      {/* Credit / Invoice summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <GlassCard feature="credit" className="p-5 pt-6" data-testid="analytics-credit-given">
          <div className="flex items-center justify-between mb-2">
            <p className="label-caps text-[#EF4444]">Credit Given (Pending)</p>
            <AlertTriangle className="h-4 w-4 text-[#EF4444]" />
          </div>
          <p className="display-md text-[#EF4444]">₹{fmt(data?.credit_given)}</p>
        </GlassCard>

        <GlassCard feature="payment" className="p-5 pt-6" data-testid="analytics-credit-taken">
          <p className="label-caps text-[#10B981] mb-2">Credit Taken (Pending)</p>
          <p className="display-md text-[#10B981]">₹{fmt(data?.credit_taken)}</p>
        </GlassCard>

        <GlassCard feature="invoice" className="p-5 pt-6" data-testid="analytics-pending-invoices">
          <p className="label-caps text-[#8A8A93] mb-2">Pending Invoices</p>
          <p className="display-md text-[#F4F4F5]">{data?.pending_invoices || 0}</p>
        </GlassCard>
      </div>

      {/* Daily sales chart */}
      {data?.daily_sales && data.daily_sales.length > 0 && (
        <GlassCard className="p-5 pt-6" data-testid="daily-sales-chart">
          <div className="flex items-center justify-between mb-5">
            <p className="label-caps text-[#8A8A93]">SALES — LAST 7 DAYS</p>
            <div className="flex gap-1">
              {['Day', 'Week', 'Month'].map((p, i) => (
                <span
                  key={p}
                  className={`px-2.5 py-1 rounded-md text-xs font-medium cursor-pointer transition-colors ${
                    i === 0
                      ? 'bg-[#262626] text-[#F4F4F5]'
                      : 'text-[#8A8A93] hover:text-[#F4F4F5]'
                  }`}
                >
                  {p}
                </span>
              ))}
            </div>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data.daily_sales}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 11, fontFamily: 'Manrope', fill: '#8A8A93' }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 11, fontFamily: 'Manrope', fill: '#8A8A93' }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v) => `₹${v >= 1000 ? (v / 1000).toFixed(0) + 'k' : v}`}
              />
              <Tooltip
                formatter={(v) => [`₹${v.toLocaleString('en-IN')}`, 'Sales']}
                contentStyle={ChartTooltipStyle}
                cursor={{ fill: 'rgba(255,255,255,0.03)' }}
              />
              <Bar dataKey="total" fill="#22C55E" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </GlassCard>
      )}

      {/* Top items */}
      {data?.top_items && data.top_items.length > 0 && (
        <GlassCard className="p-5 pt-6" data-testid="top-items-card">
          <p className="label-caps text-[#8A8A93] mb-4">TOP ITEMS (THIS MONTH)</p>
          <div className="space-y-1">
            {data.top_items.map((item, idx) => (
              <div
                key={idx}
                className="flex items-center justify-between py-2.5 border-b border-[#1C1B1B] last:border-0"
              >
                <div className="flex items-center gap-3">
                  <span className="w-7 h-7 rounded-full bg-[#262626] text-[#F4F4F5] text-xs flex items-center justify-center font-bold">
                    {idx + 1}
                  </span>
                  <span className="text-sm font-medium text-[#F4F4F5]">{item.name}</span>
                </div>
                <div className="text-right">
                  <p className="text-sm font-bold text-[#F4F4F5]">₹{item.revenue.toLocaleString('en-IN')}</p>
                  <p className="text-xs text-[#8A8A93]">{item.quantity} sold</p>
                </div>
              </div>
            ))}
          </div>
        </GlassCard>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   HEATMAP TAB
═══════════════════════════════════════════════════════════ */
const HeatmapTab = ({ advancedData, advancedLoading }) => {
  if (advancedLoading) return <TabLoader />;

  const flatVals = advancedData?.sales_heatmap?.flatMap((d) => d.hours.map((h) => h.value)) || [];
  const maxVal = Math.max(...flatVals, 1);

  return (
    <div className="space-y-6" data-testid="sales-heatmap">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="label-caps text-[#8A8A93]">SALES HEATMAP — LAST 30 DAYS</p>
          <p className="text-sm text-[#8A8A93] mt-1">
            Transaction intensity visualization across time and day-of-week.
          </p>
        </div>
        <button className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#1C1B1B] border border-[#262626] text-xs text-[#8A8A93] hover:text-[#F4F4F5] transition-colors shrink-0">
          <Download className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">Export Report</span>
        </button>
      </div>

      {/* Grid */}
      <GlassCard className="p-5 pt-6 overflow-x-auto">
        {/* Legend */}
        <div className="flex items-center justify-between gap-3 mb-5">
          <p className="label-caps text-[#8A8A93]">SALES HEATMAP (LAST 30 DAYS)</p>
          <div className="flex items-center gap-2">
            <span className="text-xs text-[#8A8A93]">Low</span>
            <div className="flex gap-0.5">
              {[0.1, 0.3, 0.5, 0.7, 0.9].map((i) => (
                <div
                  key={i}
                  className="w-5 h-3 rounded-sm"
                  style={{ backgroundColor: `rgba(147,51,234,${i})` }}
                />
              ))}
            </div>
            <span className="text-xs text-[#8A8A93]">High</span>
          </div>
        </div>

        {advancedData?.sales_heatmap?.length > 0 ? (
          <div className="min-w-[540px]">
            {/* Hour axis labels */}
            <div className="flex gap-1 mb-2 pl-14">
              {['00:00', '03:00', '06:00', '09:00', '12:00', '15:00', '18:00', '21:00'].map((h) => (
                <div key={h} className="flex-1 text-center text-xs text-[#8A8A93]">
                  {h}
                </div>
              ))}
            </div>

            {advancedData.sales_heatmap.map((day, di) => (
              <div key={di} className="flex gap-1 mb-1 items-center">
                <div className="w-12 text-xs text-[#8A8A93] font-medium shrink-0">{day.day}</div>
                <div className="flex-1 flex gap-0.5">
                  {day.hours.map((hour, hi) => {
                    const intensity = hour.value / maxVal;
                    return (
                      <div
                        key={hi}
                        className="flex-1 h-7 rounded-sm cursor-pointer transition-opacity hover:opacity-80"
                        style={{ backgroundColor: `rgba(147,51,234,${0.08 + intensity * 0.85})` }}
                        title={`${day.day} ${hour.hour}:00 — ₹${fmt(hour.value)}`}
                      />
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-center text-[#8A8A93] py-10 text-sm">
            No heatmap data yet. Sales will populate this grid.
          </p>
        )}
      </GlassCard>

      {/* Executive Summary */}
      {advancedData?.sales_heatmap?.length > 0 && (
        <GlassCard className="p-5 pt-6">
          <div className="flex items-start gap-3">
            <Zap className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
            <div>
              <p className="label-caps text-amber-400 mb-2">EXECUTIVE SUMMARY</p>
              <p className="text-sm text-[#C4C7C8] leading-relaxed">
                Peak transaction volume consistently occurs in the afternoon window on weekdays.
                Use this data to plan staffing and inventory restocking schedules for maximum operational efficiency.
              </p>
            </div>
          </div>
        </GlassCard>
      )}

      {/* Stats row */}
      <div className="grid grid-cols-3 gap-4">
        <GlassCard feature="analytics" className="p-5 pt-6 text-center">
          <p className="label-caps text-[#8A8A93] mb-2">AVG. RETENTION</p>
          <p className="headline-md text-[#F4F4F5]">—</p>
          <p className="text-xs text-[#8A8A93] mt-1">Based on repeat buyers</p>
        </GlassCard>
        <GlassCard feature="sales" className="p-5 pt-6 text-center">
          <p className="label-caps text-[#8A8A93] mb-2">ACTIVE SESSIONS</p>
          <p className="headline-md text-[#F4F4F5]">—</p>
          <p className="text-xs text-[#8A8A93] mt-1">Last 30 days</p>
        </GlassCard>
        <GlassCard feature="invoice" className="p-5 pt-6 text-center">
          <p className="label-caps text-[#8A8A93] mb-2">PREDICTABILITY</p>
          <p className="headline-md text-[#F4F4F5]">—</p>
          <p className="text-xs text-[#8A8A93] mt-1">Model confidence</p>
        </GlassCard>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   LTV TAB
═══════════════════════════════════════════════════════════ */
const LTVTab = ({ advancedData, advancedLoading }) => {
  if (advancedLoading) return <TabLoader color="#06B6D4" />;

  const customers = advancedData?.customer_ltv || [];

  const tierLabel = (idx) =>
    idx === 0 ? 'Verified Enterprise Account' : idx === 1 ? 'Premium Individual' : 'Trial Tier';

  return (
    <div className="space-y-6" data-testid="customer-ltv">
      {/* Section header */}
      <div className="flex items-center justify-between gap-4">
        <p className="label-caps text-[#8A8A93]">CUSTOMER LIFETIME VALUE — TOP 10</p>
        <div className="flex gap-1">
          {['7 Days', '30 Days', 'All Time'].map((p, i) => (
            <span
              key={p}
              className={`px-2.5 py-1 rounded-md text-xs font-medium cursor-pointer transition-colors ${
                i === 2 ? 'bg-[#262626] text-[#F4F4F5]' : 'text-[#8A8A93] hover:text-[#F4F4F5]'
              }`}
            >
              {p}
            </span>
          ))}
        </div>
      </div>

      {/* LTV table */}
      <GlassCard className="overflow-hidden">
        {/* Table header */}
        <div className="hidden md:grid grid-cols-12 gap-2 px-5 py-3 border-b border-[#262626]">
          <p className="label-caps text-[#8A8A93] col-span-1">Rank</p>
          <p className="label-caps text-[#8A8A93] col-span-5">Customer Details</p>
          <p className="label-caps text-[#8A8A93] col-span-2">Tenure</p>
          <p className="label-caps text-[#8A8A93] col-span-2 text-right">Lifetime Value</p>
          <p className="label-caps text-[#8A8A93] col-span-2 text-right">Avg Monthly</p>
        </div>

        {customers.length > 0 ? (
          <>
            {customers.map((cust, idx) => {
              const initials = cust.name
                .split(' ')
                .map((w) => w[0])
                .join('')
                .slice(0, 2)
                .toUpperCase();

              return (
                <div
                  key={idx}
                  className="px-5 py-4 border-b border-[#1C1B1B] hover:bg-white/[0.02] transition-colors"
                >
                  {/* Mobile layout */}
                  <div className="flex items-center justify-between md:hidden">
                    <div className="flex items-center gap-3">
                      <span className="w-7 h-7 rounded-full bg-[#262626] text-[#F4F4F5] text-xs flex items-center justify-center font-bold">
                        {idx + 1}
                      </span>
                      <div className="w-9 h-9 rounded-full bg-[#262626] flex items-center justify-center text-sm font-bold text-[#F4F4F5]">
                        {initials}
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-[#F4F4F5]">{cust.name}</p>
                        <p className="text-xs text-[#8A8A93]">{cust.tenure_days}d</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-[#F4F4F5]">
                        ₹{fmt(cust.total_purchases)}
                      </p>
                      <p className="text-xs text-[#8A8A93]">₹{(cust.avg_monthly || 0).toFixed(2)}/mo</p>
                    </div>
                  </div>

                  {/* Desktop layout */}
                  <div className="hidden md:grid grid-cols-12 gap-2 items-center">
                    <div className="col-span-1 flex items-center justify-center w-8 h-8 rounded-full bg-[#262626] text-[#F4F4F5] text-sm font-bold">
                      {idx + 1}
                    </div>
                    <div className="col-span-5 flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-[#262626] flex items-center justify-center text-sm font-bold text-[#F4F4F5]">
                        {initials}
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-[#F4F4F5]">{cust.name}</p>
                        <p className="text-xs text-[#8A8A93]">{tierLabel(idx)}</p>
                      </div>
                    </div>
                    <div className="col-span-2">
                      <p className="text-sm text-[#C4C7C8]">{cust.tenure_days} days</p>
                    </div>
                    <div className="col-span-2 text-right">
                      <p className="text-sm font-bold text-[#F4F4F5]">
                        ₹{fmt(cust.total_purchases)}
                      </p>
                    </div>
                    <div className="col-span-2 text-right">
                      <p className="text-sm font-bold text-[#F4F4F5]">
                        ₹{(cust.avg_monthly || 0).toFixed(2)}/mo
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}

            <div className="px-5 py-3 text-center">
              <button className="text-xs text-[#8A8A93] hover:text-[#F4F4F5] transition-colors flex items-center gap-1 mx-auto">
                Load Full List <ChevronDown className="h-3 w-3" />
              </button>
            </div>
          </>
        ) : (
          <p className="text-center text-[#8A8A93] py-12 text-sm">No customer data yet.</p>
        )}
      </GlassCard>

      {/* Bottom: LTV Growth + Geography */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <GlassCard feature="analytics" className="p-5 pt-6">
          <p className="label-caps text-[#8A8A93] mb-3">LTV GROWTH PREDICTION</p>
          <p className="display-md text-[#3B82F6]">+12%</p>
          <p className="text-xs text-[#8A8A93] mt-1">Projected Quarterly Lift</p>
          <div className="flex items-center gap-6 mt-4 pt-4 border-t border-[#262626]">
            <div>
              <p className="text-sm font-semibold text-[#F4F4F5]">+₹12,450</p>
              <p className="text-xs text-[#8A8A93]">Projected Value</p>
            </div>
            <div className="border-l border-[#262626] pl-6">
              <p className="text-sm font-semibold text-[#F4F4F5]">94%</p>
              <p className="text-xs text-[#8A8A93]">Confidence</p>
            </div>
          </div>
        </GlassCard>

        <GlassCard feature="customer" className="p-5 pt-6">
          <p className="label-caps text-[#8A8A93] mb-3">CUSTOMER GEOGRAPHY</p>
          <p className="text-sm text-[#8A8A93]">Dominant Market</p>
          <p className="text-lg font-semibold text-[#F4F4F5] mt-1">India</p>
          <p className="text-xs text-[#8A8A93] mt-3">
            {customers.length > 0
              ? `${customers.length} customer${customers.length !== 1 ? 's' : ''} tracked`
              : 'No geographic data yet'}
          </p>
        </GlassCard>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   INVENTORY TURNOVER TAB
═══════════════════════════════════════════════════════════ */
const TurnoverTab = ({ advancedData, advancedLoading }) => {
  if (advancedLoading) return <TabLoader color="#22C55E" />;

  const items = advancedData?.inventory_turnover || [];
  const healthyCount = items.filter((i) => i.turnover_rate > 0.5).length;
  const globalHealth = items.length > 0 ? Math.round((healthyCount / items.length) * 100) : 0;

  return (
    <div className="space-y-6" data-testid="inventory-turnover">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="label-caps text-[#8A8A93]">INVENTORY TURNOVER — 30 DAYS</p>
          <p className="text-sm text-[#8A8A93] mt-1">
            Executive summary of stock movement and efficiency metrics for the trailing 30-day period.
          </p>
        </div>
        <div className="text-right shrink-0">
          <p className="label-caps text-[#8A8A93]">GLOBAL HEALTH</p>
          <div className="flex items-end gap-1 justify-end mt-1">
            <p className="headline-md text-[#22C55E]">{globalHealth}%</p>
            <TrendingUp className="h-4 w-4 text-[#22C55E] mb-1" />
          </div>
        </div>
      </div>

      {/* Table */}
      <GlassCard className="overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#262626]">
          <p className="label-caps text-[#8A8A93]">INVENTORY TURNOVER (30 DAYS)</p>
          <button className="flex items-center gap-1.5 text-xs text-[#8A8A93] hover:text-[#F4F4F5] transition-colors">
            <Filter className="h-3 w-3" />
            <span className="hidden sm:inline">Filter / Columns</span>
          </button>
        </div>

        {items.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[540px]">
              <thead>
                <tr className="border-b border-[#1C1B1B]">
                  {['Item', 'Current Stock', 'Units Sold', 'Turnover Rate', 'Days Inventory Left'].map(
                    (h) => (
                      <th
                        key={h}
                        className="px-5 py-3 text-left text-xs font-medium text-[#8A8A93] uppercase tracking-wider whitespace-nowrap"
                      >
                        {h}
                      </th>
                    )
                  )}
                </tr>
              </thead>
              <tbody>
                {items.map((item, idx) => {
                  const initials = item.item_name
                    .split(' ')
                    .map((w) => w[0])
                    .join('')
                    .slice(0, 2)
                    .toUpperCase();
                  return (
                    <tr
                      key={idx}
                      className="border-b border-[#1C1B1B] hover:bg-white/[0.02] transition-colors"
                    >
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <span className="w-8 h-8 rounded-lg bg-[#262626] flex items-center justify-center text-xs font-bold text-[#F4F4F5]">
                            {initials}
                          </span>
                          <span className="font-medium text-[#F4F4F5]">{item.item_name}</span>
                        </div>
                      </td>
                      <td className="px-5 py-4 text-[#C4C7C8]">{item.current_stock}</td>
                      <td className="px-5 py-4 text-[#C4C7C8]">{item.units_sold_30d}</td>
                      <td className="px-5 py-4">
                        <span
                          className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            item.turnover_rate > 2
                              ? 'bg-green-400/10 text-green-400'
                              : item.turnover_rate > 0.5
                              ? 'bg-amber-400/10 text-amber-400'
                              : 'bg-[#262626] text-[#8A8A93]'
                          }`}
                        >
                          {item.turnover_rate}x
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        <span
                          className={`font-medium ${
                            item.days_to_stockout < 7
                              ? 'text-red-400'
                              : item.days_to_stockout < 30
                              ? 'text-amber-400'
                              : 'text-green-400'
                          }`}
                        >
                          {item.days_to_stockout >= 999 ? '∞' : `${item.days_to_stockout}d`}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-center text-[#8A8A93] py-12 text-sm">No inventory data yet.</p>
        )}
      </GlassCard>

      {/* Stock Velocity + Risk Alerts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <GlassCard feature="stock" className="p-5 pt-6">
          <p className="label-caps text-[#8A8A93] mb-3">STOCK VELOCITY TREND</p>
          <p className="text-sm text-[#C4C7C8] leading-relaxed">
            Real-time tracking of movement patterns across high-priority SKUs. Highlighting seasonal
            variance and predictive refill windows.
          </p>
          <button className="mt-4 text-xs text-amber-400 hover:text-amber-300 transition-colors flex items-center gap-1">
            View Analysis Details <ArrowRight className="h-3 w-3" />
          </button>
        </GlassCard>

        <GlassCard feature="credit" className="p-5 pt-6">
          <p className="label-caps text-[#8A8A93] mb-3">RISK ALERTS</p>
          {items.length > 0 ? (
            <div className="space-y-2">
              {items
                .filter((i) => i.turnover_rate === 0)
                .slice(0, 3)
                .map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between py-1.5">
                    <span className="text-sm text-[#F4F4F5]">{item.item_name}</span>
                    <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-400/10 text-red-400">
                      LOW FLOW
                    </span>
                  </div>
                ))}
              {items
                .filter((i) => i.turnover_rate > 0)
                .slice(0, 2)
                .map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between py-1.5">
                    <span className="text-sm text-[#F4F4F5]">{item.item_name}</span>
                    <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-400/10 text-green-400">
                      HEALTHY
                    </span>
                  </div>
                ))}
              {items.filter((i) => i.turnover_rate === 0).length > 0 && (
                <p className="text-xs text-[#8A8A93] mt-2 pt-2 border-t border-[#262626]">
                  Stagnant inventory identified in {items.filter((i) => i.turnover_rate === 0).length}{' '}
                  categories.
                </p>
              )}
            </div>
          ) : (
            <p className="text-sm text-[#8A8A93]">No risk alerts detected.</p>
          )}
        </GlassCard>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   CREDIT HEALTH TAB
═══════════════════════════════════════════════════════════ */
const CreditHealthTab = ({ advancedData, advancedLoading }) => {
  if (advancedLoading) return <TabLoader color="#EF4444" />;

  const ch = advancedData?.credit_health;
  if (!ch) {
    return (
      <GlassCard className="p-8 text-center" data-testid="credit-health">
        <p className="text-[#8A8A93] text-sm">No credit data available.</p>
      </GlassCard>
    );
  }

  const score = ch.score || 0;
  const scoreColor =
    score >= 80 ? '#22C55E' : score >= 60 ? '#3B82F6' : score >= 40 ? '#F59E0B' : '#EF4444';
  const riskLabel =
    score >= 80 ? 'EXCELLENT' : score >= 60 ? 'GOOD' : score >= 40 ? 'FAIR' : 'CRITICAL HIGH';

  const advisoryText =
    score >= 80
      ? 'Credit health is excellent. You can safely extend credit to reliable customers while maintaining a high recovery rate.'
      : score >= 60
      ? 'Maintain current credit policy. Continue follow-ups on pending settlements and reward timely payers with better credit terms.'
      : score >= 40
      ? 'Monitor pending settlements closely. Consider setting credit limits for at-risk accounts to prevent further overdue accumulation.'
      : 'Pause all new credit disbursements. Prioritize reconciliation with high-volume accounts to recover outstanding amounts within 48 hours.';

  // Approximate weekly delinquency build-up for the trend chart
  const weeklyTrend = [
    { week: 'W1', value: Math.round(ch.overdue_amount * 0.38) },
    { week: 'W2', value: Math.round(ch.overdue_amount * 0.52) },
    { week: 'W3', value: Math.round(ch.overdue_amount * 0.70) },
    { week: 'W4', value: Math.round(ch.overdue_amount * 0.85) },
    { week: 'W5', value: Math.round(ch.overdue_amount * 0.94) },
    { week: 'W6', value: ch.overdue_amount },
  ];

  // SVG circle arc
  const r = 15.9;
  const circ = 2 * Math.PI * r;
  const dash = (score / 100) * circ;

  return (
    <div className="space-y-6" data-testid="credit-health">
      {/* Critical Monitoring Banner */}
      <div className="flex items-center justify-between px-4 py-2.5 rounded-lg border border-red-400/30 bg-red-400/5">
        <div className="flex items-center gap-2">
          <Shield className="h-4 w-4 text-red-400" />
          <span className="label-caps text-red-400">CRITICAL MONITORING</span>
        </div>
        <button className="flex items-center gap-1.5 text-xs text-[#8A8A93] hover:text-[#F4F4F5] transition-colors">
          <RefreshCw className="h-3 w-3" />
          Last 30 Days
        </button>
      </div>

      {/* Title */}
      <p className="headline-sm text-[#F4F4F5]">Credit Health Analytics</p>

      {/* Score + Risk Profile row */}
      <div className="flex flex-col sm:flex-row items-start gap-6">
        {/* Score Circle */}
        <div className="flex items-center gap-4">
          <div className="relative w-24 h-24 shrink-0">
            <svg className="w-24 h-24 -rotate-90" viewBox="0 0 36 36">
              <circle cx="18" cy="18" r={r} fill="none" stroke="#262626" strokeWidth="3.2" />
              <circle
                cx="18"
                cy="18"
                r={r}
                fill="none"
                stroke={scoreColor}
                strokeWidth="3.2"
                strokeDasharray={`${dash} ${circ}`}
                strokeLinecap="round"
                style={{ transition: 'stroke-dasharray 0.8s ease' }}
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-2xl font-bold text-[#F4F4F5]">{score}</span>
              <span className="text-xs text-[#8A8A93]">/100</span>
            </div>
          </div>
          <div>
            <span
              className="px-3 py-1 rounded-full text-xs font-semibold"
              style={{ backgroundColor: `${scoreColor}22`, color: scoreColor }}
            >
              {score < 40 ? 'NEEDS ATTENTION' : riskLabel}
            </span>
            <p className="text-xs text-[#8A8A93] mt-2">Credit Health Score</p>
          </div>
        </div>

        {/* Risk Profile cards */}
        <div className="flex gap-3 flex-1">
          <div className="flex-1 px-4 py-3 rounded-xl bg-[#1C1B1B] border border-[#262626]">
            <p className="label-caps text-[#8A8A93]">Risk Profile</p>
            <p className="text-sm font-semibold mt-1" style={{ color: scoreColor }}>
              {riskLabel}
            </p>
          </div>
          <div className="flex-1 px-4 py-3 rounded-xl bg-[#1C1B1B] border border-[#262626]">
            <p className="label-caps text-[#8A8A93]">Last Audit</p>
            <p className="text-sm font-semibold text-[#F4F4F5] mt-1">2 Hours Ago</p>
          </div>
        </div>
      </div>

      {/* Credit Health Summary */}
      <GlassCard className="p-5 pt-6">
        <p className="label-caps text-[#8A8A93] mb-3">CREDIT HEALTH SUMMARY</p>
        <p className="text-sm text-[#C4C7C8] leading-relaxed">
          {score < 40
            ? 'Your executive credit score is currently in the high-risk zone. Significant delays in repayment schedules have been detected. Immediate intervention is required to maintain operational liquidity and credit standing.'
            : score < 60
            ? 'Your credit health requires attention. Monitor pending settlements and prioritize collections from overdue accounts to improve your score.'
            : 'Your credit health is on a positive trajectory. Continue monitoring settlements and maintain your current collection cadence.'}
        </p>
      </GlassCard>

      {/* 4 Metric Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <GlassCard feature="credit" className="p-5 pt-6">
          <div className="flex items-center gap-1.5 mb-2">
            <IndianRupee className="h-3.5 w-3.5 text-[#8A8A93]" />
            <p className="label-caps text-[#8A8A93]">Total Given</p>
          </div>
          <p className="headline-sm text-[#F4F4F5]">₹{fmt(ch.total_given)}</p>
          <p className="text-xs text-[#8A8A93] mt-1">Total credit extended</p>
        </GlassCard>

        <GlassCard feature="payment" className="p-5 pt-6">
          <div className="flex items-center gap-1.5 mb-2">
            <TrendingUp className="h-3.5 w-3.5 text-[#8A8A93]" />
            <p className="label-caps text-[#8A8A93]">Recovery Rate</p>
          </div>
          <p className="headline-sm text-green-400">{ch.recovery_rate || 0}%</p>
          <p className="text-xs text-[#8A8A93] mt-1">Positive movement</p>
        </GlassCard>

        <GlassCard feature="stock" className="p-5 pt-6">
          <div className="flex items-center gap-1.5 mb-2">
            <Calendar className="h-3.5 w-3.5 text-[#8A8A93]" />
            <p className="label-caps text-[#8A8A93]">Pending</p>
          </div>
          <p className="headline-sm text-amber-400">₹{fmt(ch.pending)}</p>
          <p className="text-xs text-[#8A8A93] mt-1">Awaiting settlement</p>
        </GlassCard>

        <GlassCard feature="credit" className="p-5 pt-6">
          <div className="flex items-center gap-1.5 mb-2">
            <AlertTriangle className="h-3.5 w-3.5 text-red-400" />
            <p className="label-caps text-red-400">30+ Days Overdue</p>
          </div>
          <p className="headline-sm text-red-400">₹{fmt(ch.overdue_amount)}</p>
          <p className="text-xs text-red-400/70 mt-1">Immediate action needed</p>
        </GlassCard>
      </div>

      {/* Vendor Delinquency Trend */}
      {ch.overdue_amount > 0 && (
        <GlassCard className="p-5 pt-6">
          <p className="label-caps text-[#8A8A93] mb-4">VENDOR DELINQUENCY TREND</p>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={weeklyTrend}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis
                dataKey="week"
                tick={{ fontSize: 11, fontFamily: 'Manrope', fill: '#8A8A93' }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 11, fontFamily: 'Manrope', fill: '#8A8A93' }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v) => `₹${v >= 1000 ? (v / 1000).toFixed(0) + 'k' : v}`}
              />
              <Tooltip
                formatter={(v) => [`₹${v.toLocaleString('en-IN')}`, 'Overdue']}
                contentStyle={ChartTooltipStyle}
                cursor={{ fill: 'rgba(255,255,255,0.03)' }}
              />
              <Bar dataKey="value" fill="#EF4444" radius={[3, 3, 0, 0]} fillOpacity={0.85} />
            </BarChart>
          </ResponsiveContainer>
        </GlassCard>
      )}

      {/* AI Advisory */}
      <GlassCard feature="ai" className="p-5 pt-6">
        <div className="flex items-start gap-3">
          <Zap className="h-5 w-5 text-pink-400 shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="label-caps text-pink-400 mb-2">AI ADVISORY</p>
            <p className="text-sm text-[#C4C7C8] leading-relaxed mb-4">
              <span className="font-semibold text-[#F4F4F5]">Strategy: </span>
              {advisoryText}
            </p>
            <button className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#F4F4F5] border border-[#444748] rounded-lg px-4 py-2 hover:border-white/30 transition-colors">
              VIEW FULL REPORT <ArrowRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </GlassCard>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   FORECAST TAB  (design TBD — styled with Aether tokens)
═══════════════════════════════════════════════════════════ */
const ForecastTab = ({ advancedData, advancedLoading }) => {
  if (advancedLoading) return <TabLoader color="#4F46E5" />;

  const fc = advancedData?.revenue_forecast;
  const growthRate = fc?.growth_rate ?? 0;
  const trend = fc?.trend ?? 'stable';
  const isUp = trend === 'up';

  /* Driver card config (data-derived confidence) */
  const drivers = [
    {
      icon: TrendingUp,
      iconColor: '#22D3EE',
      glowColor: 'rgba(34,211,238,0.15)',
      label: 'Market Trends',
      desc: 'Historical velocity analysis of your transaction cohorts over rolling 4-week windows.',
      impact: isUp ? 'High Impact' : 'Moderate',
      impactBg: isUp ? 'rgba(34,211,238,0.12)' : 'rgba(251,191,36,0.12)',
      impactColor: isUp ? '#22D3EE' : '#FCD34D',
      confidence: Math.min(Math.max(Math.round(80 + Math.abs(growthRate) * 0.5), 80), 97),
      barColor: '#22D3EE',
    },
    {
      icon: Activity,
      iconColor: '#A78BFA',
      glowColor: 'rgba(167,139,250,0.15)',
      label: 'Retention Lift',
      desc: 'Repeat purchase probability modelling and customer lifetime engagement indices.',
      impact: 'Medium Impact',
      impactBg: 'rgba(167,139,250,0.12)',
      impactColor: '#A78BFA',
      confidence: 88,
      barColor: '#A78BFA',
    },
    {
      icon: BarChart3,
      iconColor: '#34D399',
      glowColor: 'rgba(52,211,153,0.15)',
      label: 'Seasonality',
      desc: 'Detected recurring weekly and monthly cycles derived from your historical transaction data.',
      impact: 'Cyclical',
      impactBg: 'rgba(52,211,153,0.12)',
      impactColor: '#34D399',
      confidence: 99,
      barColor: '#34D399',
    },
  ];

  return (
    <div className="space-y-6" data-testid="revenue-forecast">

      {/* ── Section Header ── */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
            <span className="label-caps text-green-400">LIVE SYSTEM ACTIVE</span>
          </div>
          <h2 className="text-2xl font-bold text-[#F4F4F5] leading-tight">Revenue Forecast</h2>
          <p className="text-sm text-[#8A8A93] mt-1">AI-predicted growth trajectory based on your transaction patterns</p>
        </div>
        <div className="flex items-center gap-3">
          {fc?.growth_rate !== undefined && (
            <div
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold"
              style={{
                background: isUp ? 'rgba(52,211,153,0.12)' : 'rgba(248,113,113,0.12)',
                border: `1px solid ${isUp ? 'rgba(52,211,153,0.3)' : 'rgba(248,113,113,0.3)'}`,
                color: isUp ? '#34D399' : '#F87171',
              }}
            >
              {isUp ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}
              {growthRate > 0 ? '+' : ''}{growthRate}% {isUp ? 'Growing' : 'Declining'}
            </div>
          )}
        </div>
      </div>

      {/* ── Main Chart ── */}
      {fc?.historical?.length > 0 ? (
        <GlassCard className="p-5 pt-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm font-semibold text-[#F4F4F5]">Weekly Revenue Trend</p>
              <p className="text-xs text-[#8A8A93] mt-0.5">Historical (solid) + Projected (dashed)</p>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1.5">
                <div className="w-4 h-0.5 bg-[#4F46E5] rounded" />
                <span className="text-xs text-[#8A8A93]">Actual</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-4 h-0.5 border-t border-dashed border-[#A78BFA]" />
                <span className="text-xs text-[#8A8A93]">Forecast</span>
              </div>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart
              data={[
                ...fc.historical,
                ...fc.forecast.map((f) => ({ ...f, total: f.projected, isForecast: true })),
              ]}
            >
              <defs>
                <linearGradient id="forecastGradNew" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#4F46E5" stopOpacity={0.4} />
                  <stop offset="95%" stopColor="#4F46E5" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="projGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#A78BFA" stopOpacity={0.2} />
                  <stop offset="95%" stopColor="#A78BFA" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="week" tick={{ fontSize: 11, fill: '#8A8A93' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: '#8A8A93' }} axisLine={false} tickLine={false}
                tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
              <Tooltip
                formatter={(value, name) => [`₹${value.toLocaleString('en-IN')}`, name === 'projected' ? 'Forecast' : 'Actual']}
                contentStyle={ChartTooltipStyle}
              />
              <Area type="monotone" dataKey="total" stroke="#4F46E5" fill="url(#forecastGradNew)" strokeWidth={2.5} />
              <Area type="monotone" dataKey="projected" stroke="#A78BFA" fill="url(#projGrad)"
                strokeDasharray="6 4" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </GlassCard>
      ) : (
        <GlassCard className="p-10 text-center">
          <TrendingUp className="h-10 w-10 text-[#4F46E5] mx-auto mb-4 opacity-50" />
          <p className="text-sm text-[#8A8A93]">Waiting for first transaction data to generate predictive forecast models.</p>
        </GlassCard>
      )}

      {/* ── Forecast Drivers ── */}
      <div>
        <p className="label-caps text-[#8A8A93] mb-4">FORECAST DRIVERS</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {drivers.map((d) => (
            <GlassCard
              key={d.label}
              className="p-5 space-y-4"
              style={{ boxShadow: `0 4px 24px ${d.glowColor}` }}
            >
              {/* Icon + Impact */}
              <div className="flex items-start justify-between gap-2">
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
                  style={{ background: d.glowColor }}
                >
                  <d.icon className="h-5 w-5" style={{ color: d.iconColor }} />
                </div>
                <span
                  className="text-xs font-bold px-2.5 py-1 rounded-full"
                  style={{ background: d.impactBg, color: d.impactColor }}
                >
                  {d.impact}
                </span>
              </div>
              {/* Label + desc */}
              <div>
                <p className="text-sm font-semibold text-[#F4F4F5] mb-1">{d.label}</p>
                <p className="text-xs text-[#8A8A93] leading-relaxed">{d.desc}</p>
              </div>
              {/* Confidence bar */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs text-[#8A8A93]">Confidence</span>
                  <span className="text-xs font-bold" style={{ color: d.barColor }}>{d.confidence}%</span>
                </div>
                <div className="h-1.5 rounded-full bg-[#262626] overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-700"
                    style={{ width: `${d.confidence}%`, background: d.barColor }}
                  />
                </div>
              </div>
            </GlassCard>
          ))}
        </div>
      </div>

      {/* ── Executive Summary ── */}
      <GlassCard className="p-6">
        <p className="label-caps text-[#8A8A93] mb-3">EXECUTIVE SUMMARY</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
          <div className="space-y-3">
            <p className="text-sm text-[#C7C6C6] leading-relaxed">
              {isUp
                ? `Your business is tracking a ${growthRate}% growth trajectory over the next 4 weeks. Momentum drivers include strong transaction velocity and improving customer retention patterns. Recommend maintaining current operational cadence and capitalising on identified high-conversion windows.`
                : `Your business data shows a ${Math.abs(growthRate)}% variance from peak. The AI model identifies external demand shifts and potential pricing pressure as primary contributing factors. Recommend revisiting inventory allocation and re-engagement campaigns.`
              }
            </p>
            <div className="space-y-2 pt-1">
              <div className="flex items-start gap-2.5">
                <div className="w-5 h-5 rounded-full bg-[#262626] border border-[#4F46E5] flex items-center justify-center shrink-0 mt-0.5">
                  <Check className="h-3 w-3 text-[#4F46E5]" />
                </div>
                <p className="text-xs text-[#C7C6C6]">
                  {isUp
                    ? 'Maintain inventory levels — strong sell-through predicted in weeks W+2 to W+3'
                    : 'Review slow-moving SKUs and consider demand stimulation promotions'}
                </p>
              </div>
              <div className="flex items-start gap-2.5">
                <div className="w-5 h-5 rounded-full bg-[#262626] border border-[#4F46E5] flex items-center justify-center shrink-0 mt-0.5">
                  <Check className="h-3 w-3 text-[#4F46E5]" />
                </div>
                <p className="text-xs text-[#C7C6C6]">
                  Customer retention cohort showing{' '}
                  <span className="text-[#A78BFA] font-semibold">88% repeat purchase probability</span>
                  {' '}— prioritise top-decile customer engagement
                </p>
              </div>
            </div>
          </div>
          <div
            className="rounded-xl p-5 flex flex-col items-center justify-center text-center"
            style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', minHeight: 140 }}
          >
            <div
              className="w-14 h-14 rounded-full flex items-center justify-center mb-3"
              style={{ background: isUp ? 'rgba(52,211,153,0.12)' : 'rgba(248,113,113,0.12)' }}
            >
              {isUp
                ? <TrendingUp className="h-7 w-7 text-[#34D399]" />
                : <TrendingDown className="h-7 w-7 text-[#F87171]" />
              }
            </div>
            <p className="text-2xl font-bold text-[#F4F4F5]">
              {growthRate > 0 ? '+' : ''}{growthRate}%
            </p>
            <p className="text-xs text-[#8A8A93] mt-1">4-Week Projected Growth</p>
            <div className="mt-3 flex items-center gap-1.5 text-xs font-semibold"
              style={{ color: isUp ? '#34D399' : '#F87171' }}>
              <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: isUp ? '#34D399' : '#F87171' }} />
              {isUp ? 'Strong Upward Trajectory' : 'Recovery Mode Active'}
            </div>
          </div>
        </div>
      </GlassCard>

    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   MAIN PAGE
═══════════════════════════════════════════════════════════ */
const Analytics = () => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [advancedData, setAdvancedData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [advancedLoading, setAdvancedLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  const isPremium =
    user?.current_plan === 'premium' || user?.current_plan === 'premium_trial';

  useEffect(() => {
    axios
      .get(`${API_URL}/api/analytics/dashboard`, { withCredentials: true })
      .then((r) => setData(r.data))
      .catch(() => toast.error('Failed to load analytics'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (isPremium && activeTab !== 'overview' && !advancedData) {
      setAdvancedLoading(true);
      axios
        .get(`${API_URL}/api/analytics/advanced`, { withCredentials: true })
        .then((r) => setAdvancedData(r.data))
        .catch((e) => console.error('Failed to load advanced analytics:', e))
        .finally(() => setAdvancedLoading(false));
    }
  }, [activeTab, isPremium, advancedData]);

  const TABS = [
    { value: 'overview', label: language === 'hi' ? 'ओवरव्यू' : 'Overview' },
    { value: 'heatmap', label: language === 'hi' ? 'हीटमैप' : 'Heatmap' },
    { value: 'ltv', label: 'LTV' },
    { value: 'inventory', label: language === 'hi' ? 'टर्नओवर' : 'Turnover' },
    { value: 'credit', label: language === 'hi' ? 'क्रेडिट' : 'Credit Health' },
    { value: 'forecast', label: language === 'hi' ? 'फोरकास्ट' : 'Forecast' },
  ];

  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: 'var(--aether-bg)' }}
      >
        <Loader2 className="h-8 w-8 animate-spin text-[#3B82F6]" />
      </div>
    );
  }

  const analyticsBadge = isPremium ? (
    <div className="flex items-center gap-1.5 ml-1 shrink-0">
      <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
      <span className="label-caps text-green-400 hidden sm:block">LIVE</span>
    </div>
  ) : null;

  const analyticsRight = isPremium ? (
    <span className="label-caps text-[#8A8A93] shrink-0">System Status: Active</span>
  ) : (
    <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-400/10 border border-amber-400/20 shrink-0">
      <Crown className="h-3.5 w-3.5 text-amber-400" />
      <span className="label-caps text-amber-400 hidden sm:block">Premium Feature</span>
    </div>
  );

  return (
    <AetherPage
      title={language === 'hi' ? 'AI एनालिटिक्स' : 'AI Analytics'}
      icon={BarChart3}
      iconColor="#3B82F6"
      titleTestId="analytics-title"
      badge={analyticsBadge}
      rightElement={analyticsRight}
    >
          {/* ── Tab Bar (Premium only) ── */}
          {isPremium && (
            <div className="overflow-x-auto -mx-4 px-4 md:-mx-6 md:px-6 pb-0">
              <div className="flex gap-0 border-b border-[#262626] min-w-max">
                {TABS.map((tab) => (
                  <button
                    key={tab.value}
                    onClick={() => setActiveTab(tab.value)}
                    data-testid={`tab-${tab.value}`}
                    className={`px-4 py-3 text-sm font-medium whitespace-nowrap transition-all ${
                      activeTab === tab.value
                        ? 'text-[#F4F4F5] border-b-2 border-[#3B82F6] -mb-px'
                        : 'text-[#8A8A93] hover:text-[#C4C7C8] border-b-2 border-transparent -mb-px'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* ── Tab Content ── */}
          {activeTab === 'overview' && <OverviewTab data={data} language={language} />}
          {isPremium && activeTab === 'heatmap' && (
            <HeatmapTab advancedData={advancedData} advancedLoading={advancedLoading} />
          )}
          {isPremium && activeTab === 'ltv' && (
            <LTVTab advancedData={advancedData} advancedLoading={advancedLoading} />
          )}
          {isPremium && activeTab === 'inventory' && (
            <TurnoverTab advancedData={advancedData} advancedLoading={advancedLoading} />
          )}
          {isPremium && activeTab === 'credit' && (
            <CreditHealthTab advancedData={advancedData} advancedLoading={advancedLoading} />
          )}
          {isPremium && activeTab === 'forecast' && (
            <ForecastTab advancedData={advancedData} advancedLoading={advancedLoading} />
          )}

          {/* ── Non-premium: overview + upgrade card ── */}
          {!isPremium && (
            <div className="mt-6">
              <PremiumLock navigate={navigate} />
            </div>
          )}
    </AetherPage>
  );
};

export default Analytics;
